import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, RotateCcw, AlertCircle } from 'lucide-react';
import { useEditorStore } from '../store/useEditorStore';
import Canvas3D from './canvas/Canvas3D';
import { useTranslation } from 'react-i18next';

const PreviewModal: React.FC = () => {
  const { t } = useTranslation();
  const { isPreviewOpen, closePreview, resetPreviewCamera, project, currentFloor } = useEditorStore();

  // 检查是否有元素
  const hasElements = useMemo(() => {
    if (!project || !currentFloor) return false;
    const floor = project.floors.find(f => f.id === currentFloor);
    return floor?.layers.some(layer => layer.elements.length > 0) ?? false;
  }, [project, currentFloor]);

  // ESC键关闭预览
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isPreviewOpen) {
        closePreview();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPreviewOpen, closePreview]);

  // 阻止body滚动
  useEffect(() => {
    if (isPreviewOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isPreviewOpen]);

  if (!isPreviewOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closePreview();
    }
  };

  const handleResetCamera = () => {
    resetPreviewCamera();
    // 强制重新渲染Canvas3D以应用默认相机位置
    closePreview();
    setTimeout(() => {
      const { openPreview } = useEditorStore.getState();
      openPreview();
    }, 0);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="relative w-[90vw] h-[90vh] bg-white rounded-lg shadow-2xl flex flex-col">
        {/* 标题栏 */}
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary-500 to-primary-600 rounded-t-lg">
          <h2 className="text-lg font-semibold text-white">{t('preview.title')}</h2>
          
          <div className="flex items-center gap-2">
            {/* 重置相机按钮 */}
            <button
              onClick={handleResetCamera}
              className="px-3 py-1.5 text-sm text-white hover:bg-primary-700 rounded flex items-center gap-1"
              title={t('preview.resetCamera')}
            >
              <RotateCcw className="w-4 h-4" />
              {t('preview.reset')}
            </button>
            
            {/* 关闭按钮 */}
            <button
              onClick={closePreview}
              className="p-1.5 text-white hover:bg-primary-700 rounded"
              title={t('preview.closeHint')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 3D渲染区域 */}
        <div className="flex-1 relative">
          {hasElements ? (
            <Canvas3D
              width={window.innerWidth * 0.9}
              height={window.innerHeight * 0.9 - 60}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-50">
              <div className="text-center text-gray-500">
                <AlertCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg mb-2">{t('preview.noContent')}</p>
                <p className="text-sm">{t('preview.noContentHint')}</p>
              </div>
            </div>
          )}
        </div>

        {/* 底部提示 */}
        <div className="px-4 py-2 bg-gray-100 rounded-b-lg border-t border-gray-200 text-xs text-gray-600 text-center">
          {t('preview.controls')}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PreviewModal;
